import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { MessageCircle, Phone, Mail, MapPin, Shield, CreditCard, RefreshCw, Truck, Gauge, Users } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CarCard } from "@/components/CarCard";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = "40746184905";
const WHATSAPP_MESSAGE = encodeURIComponent("Salut! Sunt interesat de o mașină de la KRZZ Auto. Îmi poți da mai multe detalii?");
const TIKTOK_URL = "https://www.tiktok.com/@krzz_auto?_r=1&_t=ZN-95CO0KIU9Ty";

// TikTok Icon Component
const TikTokIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Car Brands for marquee - Text-based elegant style
const carBrands = ["AUDI", "BMW", "MERCEDES", "VOLKSWAGEN", "FORD", "PORSCHE", "OPEL", "RENAULT", "ŠKODA", "TOYOTA", "PEUGEOT", "HYUNDAI"];

// Brands Marquee Component - Interactive
const BrandsMarquee = ({ availableBrands = [] }) => {
  const navigate = useNavigate();

  const handleBrandClick = (displayName) => {
    const matched = availableBrands.find(b =>
      b.toLowerCase().includes(displayName.toLowerCase()) ||
      displayName.toLowerCase().includes(b.toLowerCase())
    );
    if (matched) {
      navigate(`/masini?brand=${encodeURIComponent(matched)}`);
    } else {
      navigate('/masini');
    }
  };

  const BrandItem = ({ brand, keyPrefix, index }) => (
    <div
      key={`${keyPrefix}-${index}`}
      className="flex-shrink-0 mx-6 lg:mx-10 flex items-center justify-center group cursor-pointer"
      onClick={() => handleBrandClick(brand)}
      data-testid={`brand-${brand.toLowerCase()}`}
    >
      <span className="text-2xl lg:text-3xl font-light tracking-[0.12em] text-gray-400 group-hover:text-white group-hover:scale-105 transition-all duration-300 select-none relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[1px] after:bg-white/50 group-hover:after:w-full after:transition-all after:duration-300">
        {brand}
      </span>
    </div>
  );

  return (
    <section className="py-10 lg:py-14 overflow-hidden bg-[#0d0d0d]" data-testid="brands-marquee">
      <div className="container-auto mb-8">
        <p className="text-center text-gray-400 text-sm lg:text-base uppercase tracking-[0.25em] font-medium">Mărci disponibile</p>
      </div>
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-20 lg:w-32 bg-gradient-to-r from-[#0d0d0d] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-20 lg:w-32 bg-gradient-to-l from-[#0d0d0d] to-transparent z-10 pointer-events-none" />
        
        <div className="flex animate-marquee">
          {carBrands.map((brand, index) => (
            <BrandItem key={`first-${index}`} brand={brand} keyPrefix="first" index={index} />
          ))}
          {carBrands.map((brand, index) => (
            <BrandItem key={`second-${index}`} brand={brand} keyPrefix="second" index={index} />
          ))}
        </div>
      </div>
      
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 35s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};

// Benefits data
const benefits = [
  {
    icon: Shield,
    title: "Garanție 12 luni",
    subtitle: "Siguranță totală pentru achiziția ta"
  },
  {
    icon: CreditCard,
    title: "Finanțare avantajoasă",
    subtitle: "Rate flexibile, aprobare rapidă"
  },
  {
    icon: RefreshCw,
    title: "Buy-Back & Trade-In",
    subtitle: "Schimbăm mașina ta veche"
  },
  {
    icon: Truck,
    title: "Import Germania",
    subtitle: "Mașini verificate din surse sigure"
  },
  {
    icon: Gauge,
    title: "Kilometraj certificat",
    subtitle: "Istoric verificat și garantat"
  },
  {
    icon: Users,
    title: "Consultanță profesională",
    subtitle: "Te ghidăm în alegerea perfectă"
  }
];

export default function HomePage() {
  const [featuredCars, setFeaturedCars] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    brand: "",
    maxPrice: ""
  });
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const carsResponse = await axios.get(`${API_URL}/api/cars/featured`);
        setFeaturedCars(carsResponse.data);
        const brandsResponse = await axios.get(`${API_URL}/api/brands`);
        setBrands(brandsResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Subtle parallax effect on mouse move (desktop only)
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (window.innerWidth < 1024) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const x = (clientX - innerWidth / 2) / innerWidth;
      const y = (clientY - innerHeight / 2) / innerHeight;
      setMousePosition({ x: x * 12, y: y * 8 });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) return;

    const startY = window.pageYOffset;
    try {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (e) {
      // Older / in-app WebViews may not support the options object
      element.scrollIntoView(true);
    }

    // Fallback for in-app browsers (e.g. TikTok WebView) where a smooth
    // scrollIntoView can silently no-op. On normal browsers the scroll has
    // already moved, so this branch is skipped and UX stays unchanged.
    window.setTimeout(() => {
      if (Math.abs(window.pageYOffset - startY) < 2) {
        const top = element.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo(0, Math.max(top, 0));
      }
    }, 350);
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (filters.brand) params.append("brand", filters.brand);
    if (filters.maxPrice) params.append("max_price", filters.maxPrice);
    window.location.href = `/masini?${params.toString()}`;
  };

  return (
    <div id="acasa" className="min-h-screen bg-[#0a0a0a]">
      <Header />

      {/* HERO - Premium Cinematic like reference */}
      <section
        ref={heroRef}
        className="relative min-h-[340px] lg:min-h-[380px] overflow-hidden pt-20 lg:pt-20"
        data-testid="hero-section"
      >
        {/* Base dark background */}
        <div className="absolute inset-0 bg-[#0a0a0a] pointer-events-none" />
        
        {/* Smoky gradient background - smooth transition from gray to black */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(ellipse 120% 100% at 25% 50%, rgba(45,45,50,0.7) 0%, rgba(30,30,35,0.5) 30%, rgba(15,15,18,0.3) 50%, transparent 70%),
              radial-gradient(ellipse 80% 80% at 20% 60%, rgba(35,35,40,0.4) 0%, transparent 50%),
              linear-gradient(to left, rgba(10,10,10,0.95) 0%, rgba(15,15,18,0.6) 30%, rgba(25,25,30,0.4) 50%, rgba(20,20,25,0.5) 70%, rgba(10,10,12,0.9) 100%)
            `,
          }}
        />

        {/* Ambient glow behind car area - LEFT side */}
        <div 
          className={`absolute left-[-10%] top-1/2 -translate-y-1/2 w-[70%] h-[100%] pointer-events-none transition-opacity duration-1000 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{
            background: 'radial-gradient(ellipse at 40% 50%, rgba(50,50,55,0.35) 0%, rgba(35,35,40,0.2) 35%, transparent 65%)',
            filter: 'blur(20px)',
          }}
        />

        {/* Secondary subtle glow for depth */}
        <div 
          className={`absolute left-[5%] top-[35%] w-[55%] h-[70%] pointer-events-none transition-opacity duration-1500 delay-200 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{
            background: 'radial-gradient(ellipse at center, rgba(40,40,45,0.2) 0%, transparent 55%)',
            filter: 'blur(50px)',
          }}
        />

        {/* Car Image - Positioned LEFT side, partially cropped */}
        <div 
          className={`absolute inset-0 pointer-events-none transition-all duration-1000 ease-out ${heroLoaded ? 'opacity-100' : 'opacity-0 -translate-x-8'}`}
          style={{
            transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
            transition: 'transform 0.4s ease-out, opacity 1s ease-out',
          }}
        >
          {/* Desktop - Car on LEFT side, better positioned */}
          <div className="hidden lg:block absolute inset-0">
            <img
              src="/images/new-homepage.webp"
              alt="KRZZ Auto"
              fetchPriority="high"
              decoding="async"
              className="absolute h-[100%] w-auto max-w-none object-contain top-1/2 -translate-y-1/2"
              style={{
                left: '-8%',
                opacity: 0.95,
                maskImage: 'linear-gradient(to right, transparent 0%, black 5%, black 60%, rgba(0,0,0,0.4) 80%, transparent 100%), linear-gradient(to bottom, black 0%, black 80%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 5%, black 60%, rgba(0,0,0,0.4) 80%, transparent 100%), linear-gradient(to bottom, black 0%, black 80%, transparent 100%)',
                WebkitMaskComposite: 'source-in',
                maskComposite: 'intersect',
              }}
            />
          </div>
          
          {/* Mobile - Car as subtle background */}
          <div className="lg:hidden absolute inset-0 flex items-center justify-center overflow-hidden">
            <img
              src="/images/homepage.webp"
              alt="KRZZ Auto"
              decoding="async"
              className="w-[150%] max-w-none h-auto object-contain"
              style={{
                transform: 'translateX(10%) translateY(5%)',
                opacity: 0.4,
                maskImage: 'linear-gradient(to top, transparent 0%, rgba(0,0,0,0.5) 20%, black 50%, black 80%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to top, transparent 0%, rgba(0,0,0,0.5) 20%, black 50%, black 80%, transparent 100%)',
              }}
            />
          </div>
        </div>

        {/* Content overlay for text readability - RIGHT side gradient */}
        <div 
          className="absolute inset-0 hidden lg:block pointer-events-none"
          style={{
            background: 'linear-gradient(to left, rgba(10,10,10,0.85) 0%, rgba(10,10,10,0.6) 25%, rgba(10,10,10,0.2) 45%, transparent 60%)',
          }}
        />

        {/* TikTok Badge - Centered on page */}
        <div className={`absolute top-20 lg:top-24 left-0 right-0 z-[60] pointer-events-none flex justify-center transition-all duration-700 delay-100 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <a
            href={TIKTOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group pointer-events-auto touch-manipulation inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#151515]/70 border border-[#2a2a2a] hover:border-[#3a3a3a] hover:bg-[#1a1a1a] transition-all duration-300 backdrop-blur-sm"
            data-testid="tiktok-badge"
          >
            <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#25F4EE] via-[#FE2C55] to-[#000] flex items-center justify-center">
              <TikTokIcon className="w-3 h-3 text-white" />
            </div>
            <span className="text-[11px] font-medium text-gray-400 group-hover:text-white transition-colors">
              Urmărește-ne pe TikTok
            </span>
            <svg className="w-2.5 h-2.5 text-gray-500 group-hover:text-white group-hover:translate-x-0.5 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        {/* Content - Centered in right half on desktop */}
        <div className="relative z-10 container-auto h-full min-h-[340px] lg:min-h-[380px] flex items-center justify-center lg:justify-end">
          <div 
            className={`w-full max-w-xl lg:max-w-lg xl:max-w-xl px-4 lg:px-0 lg:mr-[8%] xl:mr-[10%] pt-10 lg:pt-0 transition-all duration-700 delay-200 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {/* Text content - Centered on mobile, right-aligned on desktop */}
            <div className="text-center lg:text-left">
              <h1 
                className={`font-bold text-3xl sm:text-4xl lg:text-[3.25rem] xl:text-[3.5rem] text-white mb-4 leading-[1.1] tracking-tight transition-all duration-700 delay-300 whitespace-nowrap ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              >
                Mașina ta începe aici
              </h1>

              <p 
                className={`text-base sm:text-lg text-gray-400 mb-6 leading-relaxed flex flex-wrap items-center gap-x-1.5 gap-y-1 justify-center lg:justify-start transition-all duration-700 delay-400 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              >
                <span>Autoturisme verificate • Import</span>
                <span className="inline-flex items-center gap-1">
                  Germania
                  <img src="/images/de.png" width="20" alt="DE" className="inline" />
                </span>
                <span>/</span>
                <span className="inline-flex items-center gap-1">
                  Belgia
                  <img src="/images/be.png" width="20" alt="BE" className="inline" />
                </span>
                <span>/</span>
                <span className="inline-flex items-center gap-1">
                  Olanda
                  <img src="/images/nl.png" width="20" alt="NL" className="inline" />
                </span>
              </p>

              <div className="flex justify-center lg:justify-start">
                <button
                  onClick={() => scrollToSection('masini')}
                  className={`hero-btn-primary transition-all duration-700 delay-500 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  data-testid="hero-cta-cars"
                >
                  Vezi stocul disponibil
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom fade gradient for smooth transition to next section */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
          style={{
            background: 'linear-gradient(to top, #0a0a0a 0%, rgba(10,10,10,0.8) 40%, transparent 100%)',
          }}
        />
      </section>

      {/* MAȘINI DISPONIBILE - Doar 3 mașini pe homepage */}
      <section id="masini" className="pt-4 lg:pt-6 pb-14 lg:pb-16 scroll-mt-20" data-testid="featured-cars-section">
        <div className="container-auto">
          <Link 
            to="/masini"
            className="group block mb-8 animate-on-scroll"
          >
            <h2 className="font-bold text-3xl lg:text-4xl text-white text-center transition-colors group-hover:text-gray-300">
              Mașini disponibile
              <span className="inline-block ml-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300">→</span>
            </h2>
          </Link>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#1a1a1a] rounded-xl overflow-hidden border border-[#2a2a2a]">
                  <div className="aspect-[4/3] bg-[#111111] animate-pulse" />
                  <div className="p-5 space-y-4">
                    <div className="h-4 bg-[#2a2a2a] rounded w-3/4 animate-pulse" />
                    <div className="h-3 bg-[#2a2a2a] rounded w-1/2 animate-pulse" />
                    <div className="h-6 bg-[#2a2a2a] rounded w-1/3 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : featuredCars.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {featuredCars.slice(0, 3).map((car, index) => (
                <div 
                  key={car.id} 
                  className="animate-on-scroll"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CarCard car={car} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-[#1a1a1a] rounded-xl border border-[#2a2a2a]">
              <p className="text-gray-400">Nu există mașini în stoc momentan.</p>
            </div>
          )}

          <div className="text-center mt-12 animate-on-scroll">
            <Link
              to="/masini"
              className="btn-secondary"
              data-testid="view-all-cars"
            >
              Vezi toate mașinile
            </Link>
          </div>
        </div>
      </section>

      {/* BENEFICII - 6 carduri premium */}
      <section className="py-12 lg:py-16" data-testid="benefits-section">
        <div className="container-auto">
          <h2 className="font-bold text-2xl lg:text-3xl text-white text-center mb-10 animate-on-scroll">
            De ce să alegi KRZZ Auto?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="group p-5 lg:p-6 rounded-xl bg-[#151515] border border-[#2a2a2a] hover:border-[#3a3a3a] hover:bg-[#1a1a1a] transition-all duration-300 animate-on-scroll"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#252525] flex items-center justify-center shrink-0 group-hover:bg-[#2a2a2a] transition-colors">
                    <benefit.icon className="w-5 h-5 text-gray-300" />
                  </div>
                  <div className="flex flex-col">
                    <h3 className="font-semibold text-white text-base mb-1">{benefit.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{benefit.subtitle}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="section-spacing bg-[#111111] border-y border-[#2a2a2a]">
        <div className="container-auto text-center animate-on-scroll">
          <h2 className="font-bold text-3xl text-white mb-4">
            Vrei ofertă rapidă?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Sună acum sau scrie pe WhatsApp.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              data-testid="cta-whatsapp"
            >
              <MessageCircle className="w-5 h-5" />
              Contactează-ne
            </a>
            <p className="text-sm text-gray-500">Garanție 12 luni</p>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="section-padding scroll-mt-20" data-testid="contact-section">
        <div className="container-auto">
          <h2 className="font-bold text-3xl text-white text-center mb-12 animate-on-scroll">
            Contact
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Contact Info */}
            <div className="space-y-4">
              {/* Persoană de contact */}
              <div 
                className="contact-card-premium animate-on-scroll"
                style={{ animationDelay: '50ms' }}
              >
                <div className="w-12 h-12 bg-[#2a2a2a] rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">Persoană de contact</p>
                  <p className="text-gray-400">Răzvan Repede</p>
                </div>
              </div>

              <a
                href="tel:0746184905"
                className="contact-card-premium animate-on-scroll"
                data-testid="contact-phone"
                style={{ animationDelay: '100ms' }}
              >
                <div className="w-12 h-12 bg-[#2a2a2a] rounded-lg flex items-center justify-center transition-colors group-hover:bg-[#3a3a3a]">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">Telefon</p>
                  <p className="text-gray-400">0746 184 905</p>
                </div>
              </a>

              <a
                href="mailto:repede_razvan@yahoo.com"
                className="contact-card-premium animate-on-scroll"
                data-testid="contact-email"
                style={{ animationDelay: '200ms' }}
              >
                <div className="w-12 h-12 bg-[#2a2a2a] rounded-lg flex items-center justify-center transition-colors group-hover:bg-[#3a3a3a]">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">Email</p>
                  <p className="text-gray-400 text-sm break-all">repede_razvan@yahoo.com</p>
                </div>
              </a>

              <div 
                className="contact-card-premium animate-on-scroll"
                style={{ animationDelay: '300ms' }}
              >
                <div className="w-12 h-12 bg-[#2a2a2a] rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white">Adresă</p>
                  <p className="text-gray-400">Strada Armatei nr. 5B, Fălticeni</p>
                </div>
              </div>
            </div>

            {/* Map */}
            <div 
              className="h-[350px] lg:h-auto rounded-xl overflow-hidden border border-[#2a2a2a] animate-on-scroll" 
              data-testid="contact-map"
              style={{ animationDelay: '400ms' }}
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d2731.8!2d26.31876564025879!3d47.45530319213867!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zNDfCsDI3JzE5LjEiTiAyNsKwMTknMDcuNiJF!5e0!3m2!1sro!2sro!4v1699999999999!5m2!1sro!2sro"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="KRZZ Auto Location"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
